import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { List, X, CaretDown } from "@phosphor-icons/react";

const navLinks = [
  {
    label: "For Individuals",
    href: "/individuals",
    children: [
      { label: "Investing & Trading", href: "/services/investing-trading" },
      { label: "Financial Planning", href: "/services/financial-planning" },
      { label: "Tax Planning", href: "/services/tax-planning" },
      { label: "Portfolio Management", href: "/services/portfolio-management" },
      { label: "Security & Custody", href: "/services/security-custody" },
    ],
  },
  {
    label: "For Businesses",
    href: "/businesses",
    children: [
      { label: "Licensing & Compliance", href: "/services/licensing-compliance" },
      { label: "Hiring & Team Building", href: "/services/hiring-team-building" },
      { label: "Development", href: "/services/development" },
      { label: "Project Management", href: "/services/project-management" },
      { label: "Tokenization & Launch", href: "/services/tokenization-launch" },
    ],
  },
  { label: "About", href: "/about" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#F5F5F2]/95 backdrop-blur-xl border-b border-[#383A46]/10 shadow-sm"
          : "bg-[#F5F5F2]/80 backdrop-blur-xl border-b border-[#383A46]/10"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="header-logo" className="flex-shrink-0">
            <img
              src="/logo-navy.svg"
              alt="BitGenix"
              className="h-8 sm:h-9"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={() => link.children && setActiveDropdown(link.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={link.href}
                  className={`font-body font-medium text-sm tracking-wide px-4 py-2 inline-flex items-center gap-1 transition-colors duration-200 ${
                    location.pathname === link.href || location.pathname.startsWith(link.href + "/")
                      ? "text-[#A7A55C]"
                      : "text-[#21324D] hover:text-[#A7A55C]"
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {link.label}
                  {link.children && <CaretDown size={14} weight="bold" />}
                </Link>
                {link.children && activeDropdown === link.label && (
                  <div className="absolute top-full left-0 pt-2 w-60 z-50">
                    <div className="bg-white border border-[#383A46]/10 shadow-lg py-2">
                      {link.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className="block px-5 py-2.5 text-sm font-body text-[#383A46] hover:text-[#A7A55C] hover:bg-[#F5F5F2] transition-colors"
                          data-testid={`subnav-${child.label.toLowerCase().replace(/\s/g, "-")}`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <Link
              to="/contact"
              className="bg-[#A7A55C] text-[#F5F5F2] hover:bg-[#8C8A4A] transition-colors duration-300 font-body font-semibold text-sm tracking-wide px-6 py-3 rounded-none"
              data-testid="header-cta-button"
            >
              Book a Consultation
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-[#21324D]"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={24} weight="bold" /> : <List size={24} weight="bold" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-[#F5F5F2] border-t border-[#383A46]/10" data-testid="mobile-menu">
          <div className="px-6 py-6 space-y-2">
            {navLinks.map((link) => (
              <div key={link.label}>
                <Link
                  to={link.href}
                  className="block py-3 text-base font-body font-medium text-[#21324D] hover:text-[#A7A55C]"
                >
                  {link.label}
                </Link>
                {link.children && (
                  <div className="pl-4 space-y-1">
                    {link.children.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        className="block py-2 text-sm font-body text-[#383A46] hover:text-[#A7A55C]"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-4">
              <Link
                to="/contact"
                className="block text-center bg-[#A7A55C] text-[#F5F5F2] hover:bg-[#8C8A4A] font-body font-semibold text-sm px-6 py-3"
              >
                Book a Consultation
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
